import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api";
import { useAuth } from "../context/AuthContext";
import { usePendingApprovals } from "../hooks/usePendingApprovals";
import { formatDistanceToNow, parseISO } from "date-fns";
import { it } from "date-fns/locale";
import { Bell, Check, CheckCheck, AlertTriangle, ClipboardCheck, Volume2, VolumeX } from "lucide-react";

// Tipi di notifica "richiesta da approvare" generati dal codice precedente (ora deprecati):
// servono solo per il pulsante di pulizia dello storico dopo l'aggiornamento.
const REQUEST_TYPES = ["leave_request", "trip_request", "overtime_request"];

const NotificationBell = () => {
  const { isAdmin, isApprover } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [expiring, setExpiring] = useState([]);
  const [soundOn, setSoundOn] = useState(() => localStorage.getItem("tb_approval_sound") !== "off");
  const { count: pendingApprovals } = usePendingApprovals(isApprover);

  const toggleSound = () => {
    const next = !soundOn;
    setSoundOn(next);
    localStorage.setItem("tb_approval_sound", next ? "on" : "off");
  };

  const loadCount = useCallback(() => {
    api.get("/notifications/unread_count").then((r) => setUnread(r.data.count)).catch(() => {});
  }, []);

  const loadExpiring = useCallback(() => {
    if (!isAdmin) { setExpiring([]); return; }
    api.get("/employees/expiring?days=30").then((r) => setExpiring(r.data)).catch(() => {});
  }, [isAdmin]);

  const loadList = useCallback(() => {
    api.get("/notifications").then((r) => setItems(r.data)).catch(() => {});
    loadExpiring();
  }, [loadExpiring]);

  useEffect(() => {
    loadCount();
    loadExpiring();
    const t = setInterval(() => { loadCount(); loadExpiring(); }, 30000);
    return () => clearInterval(t);
  }, [loadCount, loadExpiring]);

  // Aggiornamento realtime (WebSocket) / evento globale: ricarica subito il conteggio.
  useEffect(() => {
    const onChanged = () => { loadCount(); if (open) loadList(); };
    window.addEventListener("tb-notifications-changed", onChanged);
    return () => window.removeEventListener("tb-notifications-changed", onChanged);
  }, [loadCount, loadList, open]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) loadList();
  };

  const markRead = async (id) => {
    await api.put(`/notifications/${id}/read`).catch(() => {});
    setItems((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
    loadCount();
  };

  const markAll = async () => {
    await api.put("/notifications/read_all").catch(() => {});
    setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    setUnread(0);
  };

  const deleteRequests = async () => {
    await api.delete("/notifications/requests").catch(() => {});
    setItems((prev) => prev.filter((n) => !REQUEST_TYPES.includes(n.type)));
    loadCount();
  };

  const hasRequestBacklog = items.some((n) => REQUEST_TYPES.includes(n.type));

  const ago = (iso) => {
    try { return formatDistanceToNow(parseISO(iso), { addSuffix: true, locale: it }); }
    catch { return ""; }
  };

  const expiryLabel = (d) => (d < 0 ? "Contratto scaduto" : d === 0 ? "Scade oggi" : `Scade tra ${d} giorni`);
  // Badge rosso:
  // - approvatori (admin/responsabile): SOLO le richieste da approvare (le notifiche
  //   informative restano nella lista ma non fanno numero rosso);
  // - dipendenti: le proprie notifiche non lette.
  // I contratti in scadenza restano visibili nella lista ma non contano nel rosso.
  const totalBadge = isApprover ? pendingApprovals : unread;

  return (
    <div className="relative">
      <button onClick={toggle} data-testid="notification-bell" className="relative w-9 h-9 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors text-gray-600">
        <Bell size={19} />
        {totalBadge > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[17px] h-[17px] px-1 bg-[#e30613] text-white text-[10px] font-bold rounded-full flex items-center justify-center">
            {totalBadge > 9 ? "9+" : totalBadge}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 shadow-xl rounded-md w-[340px] max-w-[90vw] z-50 overflow-hidden">
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100 bg-[#16345c] text-white">
              <span className="text-[13px] font-bold uppercase tracking-wide">Notifiche</span>
              <div className="flex items-center gap-3">
                {isApprover && (
                  <button onClick={toggleSound} data-testid="approval-sound-toggle" title={soundOn ? "Suono avvisi attivo" : "Suono avvisi disattivato"}
                    className="flex items-center gap-1 text-[11px] font-semibold text-gray-200 hover:text-white transition-colors">
                    {soundOn ? <Volume2 size={14} /> : <VolumeX size={14} />}
                    <span className="hidden sm:inline">{soundOn ? "Suono ON" : "Suono OFF"}</span>
                  </button>
                )}
                {hasRequestBacklog && (
                  <button onClick={deleteRequests} data-testid="clear-request-backlog" title="Cancella definitivamente le vecchie notifiche di richieste da approvare" className="flex items-center gap-1 text-[11px] font-semibold text-gray-200 hover:text-white transition-colors">
                    <ClipboardCheck size={13} /> Cancella richieste evase
                  </button>
                )}
                {items.some((n) => !n.read) && (
                  <button onClick={markAll} className="flex items-center gap-1 text-[11px] font-semibold text-gray-200 hover:text-white transition-colors">
                    <CheckCheck size={13} /> Segna tutte
                  </button>
                )}
              </div>
            </div>
            <div className="max-h-[360px] overflow-y-auto" data-testid="notification-list">
              {isApprover && pendingApprovals > 0 && (
                <button
                  onClick={() => { setOpen(false); navigate("/approvazioni"); }}
                  data-testid="approvals-notif-entry"
                  className="w-full text-left px-4 py-3 border-b border-gray-100 flex items-start gap-3 bg-emerald-50 hover:bg-emerald-100 transition-colors"
                >
                  <span className="w-6 h-6 rounded-full flex items-center justify-center shrink-0 bg-emerald-100 text-emerald-700">
                    <ClipboardCheck size={14} />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-bold text-[#16345c]">Richieste da approvare</div>
                    <div className="text-[12.5px] text-gray-600 leading-snug">
                      Hai <b>{pendingApprovals}</b> {pendingApprovals === 1 ? "richiesta in attesa" : "richieste in attesa"} del tuo team
                    </div>
                  </div>
                  <span className="shrink-0 min-w-[20px] h-5 px-1 rounded-full bg-[#e30613] text-white text-[11px] font-black flex items-center justify-center">{pendingApprovals > 99 ? "99+" : pendingApprovals}</span>
                </button>
              )}
              {isAdmin && expiring.map((e) => (
                <button
                  key={`exp-${e.id}`}
                  onClick={() => { setOpen(false); navigate("/admin"); }}
                  data-testid={`contract-notif-${e.id}`}
                  className="w-full text-left px-4 py-3 border-b border-gray-100 flex items-start gap-3 bg-amber-50 hover:bg-amber-100 transition-colors"
                >
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${e.days_left < 0 ? "bg-red-100 text-red-600" : "bg-amber-100 text-amber-600"}`}>
                    <AlertTriangle size={14} />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-bold text-[#16345c]">{e.days_left < 0 ? "Contratto scaduto" : "Contratto in scadenza"}</div>
                    <div className="text-[12.5px] text-gray-600 leading-snug">
                      <b>{e.name}</b>{e.is_external ? " (esterno)" : ""} · {expiryLabel(e.days_left)}
                    </div>
                    <div className="text-[11px] text-gray-400 mt-1">Scadenza {new Date(e.contract_end).toLocaleDateString("it-IT")}</div>
                  </div>
                </button>
              ))}
              {items.length === 0 && !(isAdmin && expiring.length > 0) && !(isApprover && pendingApprovals > 0) ? (
                <div className="p-8 text-center text-gray-400 text-[13px]">
                  <Bell size={28} className="mx-auto mb-2 opacity-40" />
                  Nessuna notifica
                </div>
              ) : (
                items.map((n) => (
                  <div key={n.id} className={`px-4 py-3 border-b border-gray-100 last:border-0 flex items-start gap-3 ${n.read ? "" : "bg-blue-50/50"}`}>
                    <span className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${n.type === "leave_approved" ? "bg-[#009640]" : n.type === "leave_rejected" ? "bg-[#e30613]" : "bg-[#16345c]"}`} />
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-bold text-[#16345c]">{n.title}</div>
                      <div className="text-[12.5px] text-gray-600 leading-snug">{n.body}</div>
                      <div className="text-[11px] text-gray-400 mt-1">{ago(n.created_at)}</div>
                    </div>
                    {!n.read && (
                      <button onClick={() => markRead(n.id)} title="Segna come letta" className="shrink-0 w-6 h-6 flex items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-[#009640] transition-colors">
                        <Check size={14} />
                      </button>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default NotificationBell;
